import { defineStore } from 'pinia'
import { computed, ref, watch, type WatchStopHandle } from 'vue'
import { connectRealtime, getApi } from '../api'
import { ApiError } from '../api/client'
import { publishSocialSignal } from '../api/posts/live'
import type { RealtimeConnection, RealtimeStatus } from '../api/realtime'
import type { CustomerStyleKey } from '../api/script/types'
import type {
  AiDraftResponse,
  CannedReply,
  CannedVia,
  ChannelFilter,
  ChatMessage,
  ConversationDetail,
  ConversationPatch,
  ConversationSummary,
  InboxSettings,
  InboxTab,
  LegacyInboxTab,
  MediaAsset,
  OrderDraft,
  PosOrder,
  RealtimeEvent,
  Scope,
  SearchHit,
  SendMessageBody,
  StaffAvailabilityEvent,
  TabCounts,
  TeamMember,
  ThreadItem,
} from '../api/types'
import {
  arrangeRows,
  countKey,
  DEFAULT_SETTINGS,
  DEFAULT_SORT,
  goneFromPage,
  GROUP_LABEL,
  matchesChannel,
  matchesScope,
  nextListChange,
  scopeFor,
  normalizeTab,
  segmentOf,
  STAFF_TONES,
  TAB_LABEL,
  tabOf,
  toSummary,
  waitLevel,
  waitSeconds,
  type ListSegment,
  type RowGroup,
  type SortMode,
} from '../lib/inbox'
import { matchesTag } from '../lib/collab'
import { loadReplyDraft, saveReplyDraft } from '../lib/replyDrafts'
import { ActivityTracker } from '../lib/activity'
import { findTemp, isLocalOnly, mediaClientId, newClientId, sendClientIds, tempId, type UiSendFlags } from '../lib/send'
import { useClock } from './clock'
import { useAiMode } from './aimode'
import { useCollab } from './collab'
import { useRouting } from './routing'
import { useScript } from './script'
import { useWebchat } from './webchat'

export type PanelTab = 'customer' | 'order' | 'history'

/** A thread item plus client-only send state (see lib/send.ts). */
export type UiItem = ThreadItem & UiSendFlags

/**
 * The typing box. Always the staff's own text (round 9 "กล่องร่าง", owner: "แยกกล่องพิมพ์กับกล่องร่าง"): the AI never
 * fills it by itself — only "ใช้ร่างนี้" (the staff's choice) moves a draft in, like a canned answer or a "/" pick.
 */
export interface ComposerState {
  text: string
  /**
   * Where the current text came from. `ai` = moved in from the draft box with "ใช้ร่างนี้" (maybe edited) ·
   * `script` = a "แนะนำให้ตอบ" suggestion moved in with "ใส่ในช่องพิมพ์" (stores/script.ts, maybe edited).
   */
  origin: 'ai' | 'canned' | 'manual' | 'script'
  /** The staff changed the draft they took (or added it to their own text). */
  edited: boolean
  cannedId: string | null
  /** How the library answer got in: "/" picker, one of the 2 buttons, or Jev's quick answer (canned_reply_uses). */
  cannedVia?: CannedVia | null
  /** The draft text "ใช้ร่างนี้" put in (to tell an edited draft from an untouched one). */
  draftText?: string | null
  /** Which draft it was: Jev's quick answer, the approved menu list, or an AI-written draft (the chip above the box). */
  draftKind?: 'quick' | 'menu' | 'ai' | null
  /** The suggestion that was moved in ("แนะนำให้ตอบ"). */
  scriptId?: string | null
  /** Its blanks as written in the box ("[ค่าส่ง]"): sending waits until the staff replaced every one. */
  scriptGaps?: string[] | null
  /** The customer's style the suggestion was shown for (ScriptStyle.key), counted with the send. */
  scriptStyle?: string | null
  /** The text is the AI's "ปรับตามแนวลูกค้า" version of suggestion `scriptId` (taken with "ใช้ร่างนี้"). */
  adapted?: boolean
  /** The text is the AI's "ให้ AI พาไปขั้นต่อไป" draft (คู่มือปิดการขาย): the send counts it as `ai_next`. */
  aiNext?: boolean
}

/**
 * "กล่องร่าง" (round 9): the AI draft sits in its own box above the typing box and is made when the staff ask
 * (POST …/draft) — or already exists when the owner turned "ร่างอัตโนมัติทุกข้อความ" on (GET …/draft = ready).
 * hidden = no box (AI not set up, imported history, AI mode) · disabled = nothing to answer yet ·
 * collapsed = the quiet "ให้ AI ร่างคำตอบ" row · generating = the AI is writing · ready = the draft to use ·
 * none = the AI chose not to draft (with why) · error = the request failed ("ลองใหม่").
 */
export type DraftPhase = 'hidden' | 'disabled' | 'collapsed' | 'generating' | 'ready' | 'none' | 'error'

export interface DraftBox {
  /** The box shows its result (ready / none / error). false = collapsed to the quiet row. */
  open: boolean
  /** The customer message the result in `details[id].ai` answers. */
  resultFor: string | null
  /** A newer customer message came: the result answered an older one (collapsed; pressing drafts again). */
  stale: boolean
  /** The request failed: calm text + "ลองใหม่". */
  error: string | null
  /** "ใช้ร่างนี้" moved this result into the typing box (collapsed row says "ใช้ร่างแล้ว"). */
  used: boolean
  /**
   * The result rewords this script suggestion ("เรียบเรียงใหม่", stores/script.ts) — not the plain draft of the chat:
   * the plain "ให้ AI ร่างคำตอบ" asks the API again instead of reopening it (owner 17 ต.ค. 69 "ไม่ผูกสคริปต์").
   */
  script?: string | null
}

/** The script id of a "ให้ AI พาไปขั้นต่อไป" draft (api engine.SUGGESTION_KINDS: counted as kind `ai_next`). */
export const AI_NEXT = 'ai_next'
/** Calm words for a draft request that did not come back. */
export const DRAFT_FAILED = 'ร่างไม่สำเร็จ ลองใหม่อีกครั้งได้'
/**
 * A draft still being written on the server (`pending`): read it again every 3 s, up to ~30 s. The WS `ai.draft`
 * usually says it is ready sooner; this is the fallback when that event is missed.
 */
export const DRAFT_POLL_MS = 3000
export const DRAFT_POLLS = 10
/** A stream of realtime events re-reads the list ~0.8 s after the last one — but never later than this after the first. */
export const REFRESH_MAX_WAIT_MS = 3000
/** A follow-up due time (or the end of its window) passed: re-read the list this long after, once the server agrees. */
export const DUE_SLACK_MS = 1500

/** Extra things a send carries besides the text (library pictures staged in the composer). */
export interface SendOptions {
  media?: MediaAsset[]
  /** Called when the server refused the send (4xx) — the composer puts the pictures back. */
  onRefused?: () => void
  /** Send even though a teammate answered this customer in the last 30 s (collision guard, stores/collab.ts). */
  force?: boolean
}

/** What GET /conversations/:id/draft said about a chat, besides the AI block merged into its detail. */
export interface AiMeta {
  status: AiDraftResponse['status']
  /** For `unavailable`: not_configured | history | no_inbound | ai_mode. */
  reason?: string | null
  runId: string | null
  forMessageId: string | null
  decision: AiDraftResponse['decision']
  classification: AiDraftResponse['classification']
}

export interface StaffInfo {
  /** Avatar colour class (s-p, s-j …) by position in the team list. */
  tone: string
  initial: string
}

export interface OrderFormState {
  addressId: string | null
  packageId: string | null
  slot: string | null
  pay: 'qr' | 'paid'
  coupon: string
  note: string
  allergyOn: boolean
  /** Bill number when the form was filled by "สั่งซ้ำ". */
  reorderFrom: string | null
}

const normPkg = (s: string): string => s.replace(/แพ็ก|·|\s/g, '')

/** Placeholder the API puts in a draft where a number could not be verified (api/app/ai/guardrails.py). */
export const NUMBER_MARK = '[เช็คตัวเลข]'

/** The toast when a suggestion's blank is still in the box ("[ค่าส่ง]"). */
export const gapFlash = (gap: string): string => `ยังมีช่อง ${gap} ที่ต้องเติมก่อนส่ง`

// ขอบเขตรายการแชท: เปิดหน้าครั้งแรกเป็น "ของฉัน" แล้วจำค่าที่เลือกล่าสุดไว้ในเบราว์เซอร์นี้
const SCOPE_KEY = 'cf-chat.scope'

function readScope(): Scope {
  try {
    const v = localStorage.getItem(SCOPE_KEY)
    return v === 'team' || v === 'mine' ? v : 'mine'
  } catch {
    return 'mine'
  }
}

function saveScope(s: Scope): void {
  try {
    localStorage.setItem(SCOPE_KEY, s)
  } catch {
    // storage blocked (private mode) — the choice just isn't remembered
  }
}

export const useInbox = defineStore('inbox', () => {
  const clock = useClock()
  const api = () => getApi()

  // ------------------------------------------------ state
  const me = ref<TeamMember | null>(null)
  const team = ref<TeamMember[]>([])
  const queueWaiting = ref(0)
  const settings = ref<InboxSettings>({ ...DEFAULT_SETTINGS })
  const available = ref(true)
  /**
   * พร้อมรับ was switched off by the system after no activity (docs/routing.md "ปิดพร้อมรับอัตโนมัติ"): the list
   * header shows a calm notice with one tap to turn it back on. `at` = when; `afterMin` = the setting then.
   */
  const autoOff = ref<{ at: string | null; afterMin: number } | null>(null)
  /** The notice the person closed (by its `at`), so the 30 s bootstrap refresh does not bring it back. */
  const autoOffDismissed = ref<string | null>(null)
  const cannedDefault = ref<CannedReply[]>([])
  /** The owner's "AI ดูแลแชท" switch (bootstrap): the empty AI tab says the AI is off. null = not known yet. */
  const aiModeEnabled = ref<boolean | null>(null)

  const convs = ref<Record<string, ConversationSummary>>({})
  const counts = ref<TabCounts>({ needs_reply: 0, waiting: 0, follow_up: 0, ai: 0 })
  const cursors = ref<Partial<Record<InboxTab, string | null>>>({})
  const tab = ref<InboxTab>('active')
  const channel = ref<ChannelFilter>('all')
  const scope = ref<Scope>(readScope())
  /** Only customers with this team label (stores/collab.ts tag definitions). */
  const tagFilter = ref<string | null>(null)
  const sortModes = ref<Record<InboxTab, SortMode>>({ ...DEFAULT_SORT })
  const listLoading = ref(false)
  const listError = ref<string | null>(null)

  const selectedId = ref<string | null>(null)
  const details = ref<Record<string, ConversationDetail>>({})
  const threads = ref<Record<string, UiItem[]>>({})
  const olderCursor = ref<Record<string, string | null>>({})
  const orderDrafts = ref<Record<string, OrderDraft | null>>({})
  const orderForms = ref<Record<string, OrderFormState>>({})
  const composers = ref<Record<string, ComposerState>>({})
  const openError = ref<string | null>(null)
  const panelTab = ref<PanelTab>('customer')
  const aiMeta = ref<Record<string, AiMeta>>({})
  /** "กล่องร่าง" state per chat (round 9). */
  const draftBoxes = ref<Record<string, DraftBox>>({})
  /** The AI is not set up on the server (a draft read said `not_configured`): no draft box anywhere, no flash. */
  const aiOff = ref(false)
  /** Wait before re-reading the draft after a customer message (with "ร่างอัตโนมัติ" on the server drafts meanwhile). */
  const draftDelayMs = ref(2500)

  const query = ref('')
  const searchHits = ref<SearchHit[] | null>(null)
  const searchPartial = ref(false)
  const searching = ref(false)

  const realtimeStatus = ref<RealtimeStatus>('closed')
  const toast = ref<string | null>(null)
  /** Debounce for re-fetching the list after realtime events. 0 disables (tests). */
  const refreshDelayMs = ref(800)

  // ------------------------------------------------ getters
  const meId = computed(() => me.value?.id ?? null)

  /** The scope the tab on screen really uses (the AI tab: always the whole team — lib/inbox.ts scopeFor). */
  const viewScope = computed(() => scopeFor(tab.value, scope.value))

  /** The list you are looking at: tab + channel + scope + team label. */
  const viewKey = computed(() => `${tab.value}|${channel.value}|${viewScope.value}|${tagFilter.value ?? ''}`)

  function inView(c: ConversationSummary, now: number = clock.now): boolean {
    return (
      tabOf(c, now) === tab.value &&
      matchesChannel(c, channel.value) &&
      matchesScope(c, viewScope.value, meId.value) &&
      matchesTag(c, tagFilter.value)
    )
  }

  /**
   * The chat you are working on stays where you opened it until you move on (owner 27 ก.ย. 69:
   * "กดตอบแล้วแชทหายไปอีกหน้า"): replying in ต้องตอบ, a customer writing while it waits for them, ปิดแชท, the AI
   * taking it or handing it back, a reassignment… keep its row where it was (sorted by `snap`, its last state in this
   * place) with a calm "ตอบแล้ว · อยู่ในรอลูกค้าตอบ". "Where" = the list segment: in กำลังคุย also its group (ต้องตอบ /
   * รอลูกค้าตอบ), so a reply does not make the row jump down the same list either.
   * It goes when you open another chat, change tab / scope / channel / label, or reload. Only ever the open chat.
   */
  const sticky = ref<{ id: string; view: string; snap: ConversationSummary } | null>(null)

  /** Anchor the open chat to the list on screen when it belongs there right now; otherwise no sticky row. */
  function anchorSticky(): void {
    const id = selectedId.value
    const c = id ? convs.value[id] : undefined
    sticky.value = id && c && inView(c) ? { id, view: viewKey.value, snap: { ...c } } : null
  }

  function liveSticky(): { id: string; snap: ConversationSummary; live: ConversationSummary } | null {
    const st = sticky.value
    if (!st || st.id !== selectedId.value || st.view !== viewKey.value) return null
    const live = convs.value[st.id]
    return live ? { id: st.id, snap: st.snap, live } : null
  }

  /** The held row is still in its place: in this list and in the same segment (group) as when it was anchored. */
  function inPlace(snap: ConversationSummary, live: ConversationSummary, now: number): boolean {
    return inView(live, now) && segmentOf(live, now) === segmentOf(snap, now)
  }

  /** The list on screen, as groups (กำลังคุย: ต้องตอบ then รอลูกค้าตอบ; other tabs: one group without a header). */
  const groups = computed<RowGroup[]>(() => {
    const now = clock.now
    const mode = sortModes.value[tab.value]
    const list = Object.values(convs.value).filter((c) => inView(c, now))
    const st = liveSticky()
    if (!st || inPlace(st.snap, st.live, now)) return arrangeRows(list, tab.value, mode)
    // moved on: keep its place (sorted as it was, in the group it was in), show the live row
    const held = [...list.filter((c) => c.id !== st.id), st.snap]
    return arrangeRows(held, tab.value, mode).map((g) => ({
      key: g.key,
      rows: g.rows.map((c) => (c.id === st.id ? st.live : c)),
    }))
  })

  const rows = computed(() => groups.value.flatMap((g) => g.rows))

  /** Where a chat is now, as the held row says it: "ต้องตอบ" · "รอลูกค้าตอบ" · "ทักตาม" · "แท็บ AI" · "แท็บปิดแล้ว". */
  function placeLabel(seg: ListSegment): string {
    if (seg === 'needs_reply' || seg === 'waiting') return GROUP_LABEL[seg]
    // "อยู่ในปิดแล้ว" / "อยู่ในAI" read oddly: name those as a tab
    return seg === 'ai' ? `แท็บ ${TAB_LABEL.ai}` : seg === 'closed' ? `แท็บ${TAB_LABEL.closed}` : TAB_LABEL[seg]
  }

  /** Why the open chat's row is still here although it moved on: "ตอบแล้ว · อยู่ในรอลูกค้าตอบ" (null = a normal row). */
  function stickyNote(id: string): string | null {
    const st = liveSticky()
    const now = clock.now
    if (!st || st.id !== id || inPlace(st.snap, st.live, now)) return null
    const live = st.live
    const from = tab.value
    const to = segmentOf(live, now)
    if (tabOf(live, now) !== from || (from === 'active' && to !== segmentOf(st.snap, now))) {
      const what =
        to === 'ai'
          ? 'AI รับดูแลแล้ว'
          : to === 'closed'
            ? 'ปิดแชทแล้ว'
            : to === 'follow_up'
              ? 'ถึงเวลาทักตาม'
              : from === 'ai'
                ? live.ai_handoff
                  ? 'AI ส่งต่อแล้ว'
                  : 'AI หยุดแล้ว'
                : to === 'needs_reply'
                  ? 'ลูกค้าทักมา'
                  : from === 'closed'
                    ? 'เปิดแชทแล้ว'
                    : from === 'follow_up'
                      ? 'ทักตามแล้ว'
                      : 'ตอบแล้ว'
      return `${what} · อยู่ใน${placeLabel(to)}`
    }
    if (!matchesScope(live, viewScope.value, meId.value)) {
      const who = live.assigned_staff
      return who ? `ย้ายให้ ${who.name} แล้ว` : 'ย้ายเข้าคิวกลางแล้ว'
    }
    return 'ไม่อยู่ในรายการนี้แล้ว'
  }

  const selected = computed(() => (selectedId.value ? (convs.value[selectedId.value] ?? null) : null))
  const selectedDetail = computed(() => (selectedId.value ? (details.value[selectedId.value] ?? null) : null))
  const selectedThread = computed(() => (selectedId.value ? (threads.value[selectedId.value] ?? null) : null))

  /** Rail dots: red when someone waits past the red threshold, green when anyone needs a reply (the AI's chats: not ours). */
  const replyAlert = computed<'none' | 'normal' | 'crit'>(() => {
    const now = clock.now
    let any = false
    for (const c of Object.values(convs.value)) {
      if (segmentOf(c, now) !== 'needs_reply') continue
      any = true
      if (waitLevel(waitSeconds(c, now), settings.value) === 'crit') return 'crit'
    }
    return any || counts.value.needs_reply > 0 ? 'normal' : 'none'
  })

  const staffMap = computed(() => {
    const m = new Map<string, StaffInfo>()
    team.value.forEach((t, i) => m.set(t.id, { tone: STAFF_TONES[i % STAFF_TONES.length] ?? 's-w', initial: t.initial }))
    return m
  })

  function staffInfo(id: string | null | undefined): StaffInfo {
    return (id ? staffMap.value.get(id) : undefined) ?? { tone: 's-w', initial: '·' }
  }

  // ------------------------------------------------ helpers
  /** The number a chat counts in for the list on screen (null = not counted: other scope/channel/label, or closed). */
  function countedTab(c: ConversationSummary, now: number): keyof TabCounts | null {
    if (!matchesChannel(c, channel.value) || !matchesTag(c, tagFilter.value)) return null
    const k = countKey(c, now)
    // the AI number is the whole team's under ของฉัน too (lib/inbox.ts scopeFor)
    return k && matchesScope(c, scopeFor(k, scope.value), meId.value) ? k : null
  }

  function upsert(s: ConversationSummary): void {
    const prev = convs.value[s.id]
    if (prev) {
      // tab counters move at once (the server's numbers replace these on the next list refresh, < 1 s)
      const now = clock.now
      const a = countedTab(prev, now)
      const b = countedTab(s, now)
      if (a !== b) {
        const n = { ...counts.value }
        if (a) n[a] = Math.max(0, (n[a] ?? 0) - 1)
        if (b) n[b] = (n[b] ?? 0) + 1
        counts.value = n
      }
    }
    // the open chat is (again) part of the list on screen: remember its place here (also when the list or the
    // chat arrives after the chat was opened, e.g. a link straight to /c/:id) — unless it is held in the other group
    // of กำลังคุย (replied in ต้องตอบ: it stays up there until you move on)
    if (s.id === selectedId.value && inView(s)) {
      const st = sticky.value
      const now = clock.now
      const held = !!st && st.id === s.id && st.view === viewKey.value && segmentOf(st.snap, now) !== segmentOf(s, now)
      if (!held) sticky.value = { id: s.id, view: viewKey.value, snap: { ...s } }
    }
    convs.value[s.id] = s
    const d = details.value[s.id]
    if (d) {
      // the open chat's header and side card follow the row: ห้ามปิดอัตโนมัติ, the follow-up switch … (own copy of the
      // follow-up object: the switch's optimistic flip must not write into the row)
      const { contact, ...rest } = s
      Object.assign(d, rest.follow_up ? { ...rest, follow_up: { ...rest.follow_up } } : rest)
      d.contact.name = contact.name
      d.contact.initial = contact.initial
      d.contact.avatar_url = contact.avatar_url ?? null
    }
    if (s.id === selectedId.value) checkBehindSoon(s.id)
  }

  /** The `last_message_at` each open chat was already re-read for (one re-read per message the thread lacks). */
  const behindRead: Record<string, string> = {}
  const behindQueued = new Set<string>()
  /** After the change that called upsert has landed (an event adds its bubble right after the upsert): check once. */
  function checkBehindSoon(id: string): void {
    if (behindQueued.has(id)) return
    behindQueued.add(id)
    queueMicrotask(() => {
      behindQueued.delete(id)
      checkBehind(id)
    })
  }

  /**
   * The open chat's row says a newer message exists than any in its loaded thread (an event was missed — a socket that
   * died quietly, a laptop that slept): read the newest page again.
   */
  function checkBehind(id: string): void {
    const at = convs.value[id]?.last_message_at
    const list = threads.value[id]
    if (id !== selectedId.value || !list || !at || behindRead[id] === at) return
    const last = Date.parse(at)
    let newest = Number.NEGATIVE_INFINITY
    for (const x of list) newest = Math.max(newest, Date.parse(x.at))
    if (!(last > newest)) return
    behindRead[id] = at
    void refreshThread(id).catch(() => undefined)
  }

  /** A thread item this screen just changed through the API (the "/ln" card): swap it in place. Other screens get
   *  the same change as message.updated. */
  function replaceItem(convId: string, item: ThreadItem): void {
    const list = threads.value[convId]
    const i = list ? list.findIndex((x) => x.id === item.id) : -1
    if (list && i >= 0) list.splice(i, 1, item as UiItem)
  }

  function flash(msg: string): void {
    toast.value = msg
    setTimeout(() => {
      if (toast.value === msg) toast.value = null
    }, 4000)
  }

  const errText = (e: unknown): string => (e instanceof Error ? e.message : String(e))

  // ------------------------------------------------ list
  let listSeq = 0
  /** The list (viewKey) whose later pages were loaded with "load more": its cursor points past page 1. */
  let deepView: string | null = null

  async function fetchList(opts: { silent?: boolean } = {}): Promise<void> {
    const q = { tab: tab.value, channel: channel.value, scope: scope.value, tag: tagFilter.value }
    const view = viewKey.value
    const seq = ++listSeq
    if (!opts.silent) {
      listLoading.value = true
      deepView = null
    }
    try {
      const page = await api().listConversations({ ...q, limit: 50 })
      if (seq !== listSeq) return
      // drop local rows the server no longer puts here (missed events): every missing one when the list is complete,
      // else the ones that would sort inside this page (later ones may be on a page that was not re-read)
      const now = clock.now
      const local = Object.values(convs.value).filter(
        (c) =>
          c.id !== selectedId.value &&
          tabOf(c, now) === q.tab &&
          matchesChannel(c, q.channel) &&
          matchesScope(c, scopeFor(q.tab, q.scope), meId.value) &&
          matchesTag(c, q.tag),
      )
      for (const id of goneFromPage(local, page.items, q.tab, page.next_cursor === null)) delete convs.value[id]
      for (const c of page.items) upsert(c)
      counts.value = page.counts
      // a refresh reads page 1 only: after "load more" the next page to load is still the later one (keyset cursor)
      if (!(opts.silent && deepView === view && page.next_cursor !== null)) cursors.value[q.tab] = page.next_cursor
      listError.value = null
    } catch (e) {
      if (seq === listSeq) listError.value = errText(e)
    } finally {
      if (seq === listSeq) listLoading.value = false
    }
  }

  async function loadMore(): Promise<void> {
    const cursor = cursors.value[tab.value]
    if (!cursor || listLoading.value) return
    const t = tab.value
    const view = viewKey.value
    listLoading.value = true
    try {
      const page = await api().listConversations({
        tab: t,
        channel: channel.value,
        scope: scope.value,
        tag: tagFilter.value,
        cursor,
        limit: 50,
      })
      if (view !== viewKey.value) return // another tab / filter meanwhile: its own fetchList owns the cursor
      for (const c of page.items) upsert(c)
      cursors.value[t] = page.next_cursor
      deepView = view
    } catch (e) {
      if (view === viewKey.value) listError.value = errText(e)
    } finally {
      if (view === viewKey.value) listLoading.value = false
    }
  }

  let refreshTimer: ReturnType<typeof setTimeout> | null = null
  /** When the burst of events now waiting for a re-read began. */
  let refreshSince = 0
  /** Re-read the list after a burst of realtime events settles (~0.8 s), but at most ~3 s after its first event. */
  function scheduleRefresh(): void {
    if (refreshDelayMs.value <= 0) return
    const now = Date.now()
    if (refreshTimer) clearTimeout(refreshTimer)
    else refreshSince = now
    const wait = Math.max(0, Math.min(refreshDelayMs.value, refreshSince + REFRESH_MAX_WAIT_MS - now))
    refreshTimer = setTimeout(() => {
      refreshTimer = null
      void fetchList({ silent: true })
    }, wait)
  }

  // changing what the list shows = moving on: the open chat is only kept when it belongs to the new list.
  // Old tab keys (ต้องตอบ / รอลูกค้า before 27 ก.ย. 69, e.g. from older code or a link) open กำลังคุย.
  function setTab(key: InboxTab | LegacyInboxTab): void {
    const t = normalizeTab(key)
    if (tab.value === t && !query.value) return
    tab.value = t
    anchorSticky()
    clearSearch()
    void fetchList()
  }
  function setChannel(c: ChannelFilter): void {
    channel.value = c
    anchorSticky()
    void fetchList()
  }
  function setScope(s: Scope): void {
    if (tab.value === 'ai') return // not a filter of the AI tab (the control is dimmed there)
    scope.value = s
    saveScope(s)
    anchorSticky()
    void fetchList()
  }
  function setTagFilter(id: string | null): void {
    if (tagFilter.value === id) return
    tagFilter.value = id
    anchorSticky()
    void fetchList()
  }
  function toggleSort(): void {
    const cur = sortModes.value[tab.value]
    sortModes.value[tab.value] = cur === 'wait' ? 'recent' : 'wait'
  }

  async function loadTeam(): Promise<void> {
    const b = await api().bootstrap()
    me.value = b.me
    team.value = b.team
    queueWaiting.value = b.queue_waiting
    settings.value = b.settings
    available.value = b.me.available
    const off = b.me.available_off
    autoOff.value =
      !b.me.available && off?.reason === 'idle' ? { at: off.at, afterMin: b.settings.auto_off_min ?? 15 } : null
    cannedDefault.value = b.canned_default
    if (typeof b.ai_mode_enabled === 'boolean') aiModeEnabled.value = b.ai_mode_enabled
  }

  // ------------------------------------------------ open a conversation
  /** The typing box of a chat — starts empty: the AI never types into it (round 9). */
  function ensureComposer(id: string): ComposerState {
    let st = composers.value[id]
    if (!st) {
      // the unsent text of this chat, kept in this browser across a reload (lib/replyDrafts.ts)
      st = {
        text: loadReplyDraft(id), origin: 'manual', edited: false, cannedId: null, cannedVia: null, draftText: null, draftKind: null,
        scriptId: null, scriptGaps: null, scriptStyle: null, adapted: false, aiNext: false,
      }
      composers.value[id] = st
    }
    return st
  }

  function ensureOrderForm(id: string): void {
    if (orderForms.value[id]) return
    const d = orderDrafts.value[id]
    const allergy = details.value[id]?.pos?.allergies.length ?? 0
    orderForms.value[id] = {
      addressId: d?.address_id ?? null,
      packageId: d?.package_id ?? null,
      slot: d?.slot ?? null,
      pay: 'qr',
      coupon: '',
      note: '',
      allergyOn: d?.allergy_filter?.on ?? allergy > 0,
      reorderFrom: null,
    }
  }

  /** Thread events heard per chat: an open whose page was on its way meanwhile re-reads the newest page. */
  const threadEvents: Record<string, number> = {}
  /** Loaded threads of chats not on screen when the socket came back (events may be missed): re-read on open. */
  const staleThreads = new Set<string>()

  let openSeq = 0
  async function select(id: string | null): Promise<void> {
    const other = selectedId.value !== id
    if (other) panelTab.value = 'customer'
    selectedId.value = id
    if (other) anchorSticky() // opening another chat = moving on: the previous one leaves if it moved
    openError.value = null
    useCollab().viewing(id) // presence: colleagues see who has this chat open
    if (!id) return
    const seq = ++openSeq
    const heard = threadEvents[id] ?? 0
    if (threads.value[id] && staleThreads.delete(id)) void refreshThread(id).catch(() => undefined)
    try {
      const [detail, page, draft] = await Promise.all([
        api().getConversation(id),
        threads.value[id] ? Promise.resolve(null) : api().listMessages(id, { limit: 50 }),
        id in orderDrafts.value ? Promise.resolve(orderDrafts.value[id] ?? null) : api().getOrderDraft(id),
      ])
      details.value[id] = detail
      // realtime may have moved on while the request was in flight: keep the newer summary
      const cur = convs.value[id]
      const fresh = !cur || (detail.last_message_at ?? '') >= (cur.last_message_at ?? '')
      upsert(fresh ? toSummary(detail) : cur)
      if (page) {
        // keep anything realtime already appended while we were loading
        const extra = (threads.value[id] ?? []).filter((x) => !page.items.some((p) => p.id === x.id))
        threads.value[id] = [...page.items, ...extra]
        olderCursor.value[id] = page.next_before
        // realtime spoke about this chat while the page was on its way (with no thread to land in): read again
        if ((threadEvents[id] ?? 0) !== heard) void refreshThread(id).catch(() => undefined)
      }
      orderDrafts.value[id] = draft
      ensureComposer(id)
      ensureOrderForm(id)
      void useCollab().loadNotes(id)
      if (seq === openSeq) void markRead(id)
      // a cheap read (round 9): a draft only if one exists; not while the staff's own request is running
      if (!convs.value[id]?.ai_mode && !draftLoading.value[id]) void loadDraft(id) // AI in charge: no draft to tempt a reply
    } catch (e) {
      if (seq === openSeq) openError.value = errText(e)
    }
  }

  const olderLoading = new Set<string>()
  async function loadOlder(id: string): Promise<void> {
    const before = olderCursor.value[id]
    if (!before || olderLoading.has(id)) return // pressed again while the page is on its way
    olderLoading.add(id)
    try {
      const page = await api().listMessages(id, { before, limit: 50 })
      const cur = threads.value[id]
      if (!cur) return
      const have = new Set(cur.map((x) => x.id))
      threads.value[id] = [...page.items.filter((x) => !have.has(x.id)), ...cur]
      olderCursor.value[id] = page.next_before
    } finally {
      olderLoading.delete(id)
    }
  }

  async function markRead(id: string): Promise<void> {
    const c = convs.value[id]
    if (!c || c.unread === 0) return
    upsert({ ...c, unread: 0 })
    try {
      await api().markRead(id)
    } catch {
      /* not critical: the server recomputes unread on the next event */
    }
  }

  // ------------------------------------------------ AI draft ("กล่องร่าง", round 9)
  function boxOf(id: string): DraftBox {
    let b = draftBoxes.value[id]
    if (!b) {
      b = { open: false, resultFor: null, stale: false, error: null, used: false, script: null }
      draftBoxes.value[id] = b
    }
    return b
  }

  /**
   * A draft answer arrived (GET read or the staff's POST): the AI block goes on the chat's detail (side panel: next
   * steps, close chance, the 2 canned buttons) and in the draft box — never into the typing box.
   * `asked` = the staff pressed the box: show the result even if it is the one they closed before.
   * `script` = the result rewords that script suggestion ("เรียบเรียงใหม่").
   */
  function applyDraft(id: string, res: AiDraftResponse, opts: { asked?: boolean; script?: string | null } = {}): void {
    const held = draftBoxes.value[id]
    // a read of the plain draft (GET) never swaps out the "เรียบเรียงใหม่" result the staff asked for on this message
    if (!opts.asked && res.status === 'ready' && held?.script && held.resultFor === res.for_message_id && details.value[id]?.ai) return
    const prev = aiMeta.value[id]
    aiMeta.value[id] = {
      status: res.status,
      reason: res.reason ?? null,
      runId: res.run_id,
      forMessageId: res.for_message_id,
      decision: res.decision,
      classification: res.classification,
    }
    aiOff.value = res.status === 'unavailable' && res.reason === 'not_configured'
    const box = boxOf(id)
    if (res.status === 'idle' || res.status === 'unavailable') {
      // a newer customer message than the one the cached result answered: back to the quiet row
      if (box.resultFor && res.for_message_id && box.resultFor !== res.for_message_id) {
        Object.assign(box, { open: false, stale: true, used: false, error: null })
      }
      return
    }
    if (res.status === 'error') {
      if (opts.asked) Object.assign(box, { open: true, stale: false, error: res.error?.trim() || DRAFT_FAILED })
      return
    }
    const d = details.value[id]
    if (!d || res.status !== 'ready' || !res.ai) return // pending: the caller asks again
    const same = !!prev && prev.runId === res.run_id && box.resultFor === res.for_message_id && !!d.ai
    if (!same) {
      // keep steps the staff already ticked
      const ticked = new Map((d.ai?.next_steps ?? []).map((x) => [x.id, x.done]))
      d.ai = { ...res.ai, next_steps: res.ai.next_steps.map((x) => ({ ...x, done: ticked.get(x.id) ?? x.done })) }
      box.used = false
    }
    box.resultFor = res.for_message_id
    box.stale = false
    box.error = null
    box.script = opts.script ?? null
    // a new result opens the box (the staff asked, or "ร่างอัตโนมัติ" made it); the one they closed stays closed
    if (!same || opts.asked) box.open = true
  }

  const draftSeq: Record<string, number> = {}
  /** The AI is writing a draft for this chat (the staff's request, or the server's own while `pending`). */
  const draftLoading = ref<Record<string, boolean>>({})

  /** Chats whose staff POST …/draft is on its way → its request number (its answer brings the draft; ai.draft waits). */
  const draftAsking: Record<string, number> = {}
  /** Chats polling a `pending` draft → whether the staff asked for it (the read that ai.draft triggers keeps that). */
  const draftPolling: Record<string, boolean> = {}

  /** GET …/draft — a read (round 9): an existing draft for the newest customer message, or `idle`. */
  async function loadDraft(id: string, opts: { retry?: number; asked?: boolean } = {}): Promise<void> {
    const seq = (draftSeq[id] = (draftSeq[id] ?? 0) + 1)
    const left = opts.retry ?? DRAFT_POLLS
    let again = false
    try {
      const res = await api().getDraft(id)
      if (seq !== draftSeq[id]) return
      applyDraft(id, res, { asked: opts.asked })
      // still being written on the server ("ร่างอัตโนมัติ" on, or the staff's run): show it being made, ask again —
      // every 3 s for up to ~30 s (WS ai.draft usually says "ready" before that)
      if (res.status === 'pending' && left > 0) {
        again = true
        draftLoading.value[id] = true
        draftPolling[id] = !!opts.asked
        setTimeout(() => {
          if (seq === draftSeq[id]) void loadDraft(id, { retry: left - 1, asked: opts.asked })
        }, DRAFT_POLL_MS)
      }
    } catch {
      /* no draft is fine: the typing box works without one */
    } finally {
      if (seq === draftSeq[id] && !again) {
        draftLoading.value[id] = false
        delete draftPolling[id]
      }
    }
  }

  /**
   * WS ai.draft: a draft of this chat is ready (the server's auto draft, or a run another screen asked for). The open
   * chat reads it now (a poll of a pending draft stops waiting); not while this screen's own request is on its way.
   */
  function onAiDraft(id: string): void {
    if (id !== selectedId.value || convs.value[id]?.ai_mode || id in draftAsking) return
    void loadDraft(id, { asked: draftPolling[id] ?? false })
  }

  /** POST …/draft — the staff pressed the draft box: draft now · `refresh` = "ร่างใหม่" (always a new run) ·
   *  `suggestionId` = "เรียบเรียงใหม่" of the script suggestion on screen (stores/script.ts) · + `adapt` =
   *  "ปรับตามแนวลูกค้า" of it · `nextStep` = "ให้ AI พาไปขั้นต่อไป" (คู่มือปิดการขาย: the box remembers it as `ai_next`). */
  async function requestDraft(
    id: string,
    opts: { refresh?: boolean; suggestionId?: string | null; adapt?: boolean; nextStep?: boolean } = {},
  ): Promise<void> {
    if (convs.value[id]?.ai_mode) return
    const seq = (draftSeq[id] = (draftSeq[id] ?? 0) + 1)
    const box = boxOf(id)
    box.error = null
    draftLoading.value[id] = true
    draftAsking[id] = seq
    let again = false
    try {
      const res = await api().requestDraft(
        id,
        opts.nextStep
          ? { refresh: opts.refresh, nextStep: true }
          : opts.suggestionId
            ? { refresh: opts.refresh, suggestionId: opts.suggestionId, ...(opts.adapt ? { adapt: true } : {}) }
            : { refresh: opts.refresh },
      )
      if (seq !== draftSeq[id]) return
      applyDraft(id, res, { asked: true, script: opts.nextStep ? AI_NEXT : (opts.suggestionId ?? null) })
      if (res.status === 'pending') {
        again = true
        draftPolling[id] = true
        setTimeout(() => {
          if (seq === draftSeq[id]) void loadDraft(id, { retry: DRAFT_POLLS - 1, asked: true })
        }, DRAFT_POLL_MS)
      }
    } catch (e) {
      if (seq !== draftSeq[id]) return
      const why = errText(e).trim()
      Object.assign(box, { open: true, stale: false, error: why ? `ร่างไม่สำเร็จ · ${why}` : DRAFT_FAILED })
    } finally {
      if (draftAsking[id] === seq) delete draftAsking[id]
      if (seq === draftSeq[id] && !again) draftLoading.value[id] = false
    }
  }

  /** What the draft box shows now. */
  function draftPhase(id: string): DraftPhase {
    if (convs.value[id]?.ai_mode ?? details.value[id]?.ai_mode) return 'hidden'
    if (details.value[id]?.is_history) return 'hidden' // imported LINE history: nothing to answer
    const meta = aiMeta.value[id]
    if (meta?.status === 'unavailable') return meta.reason === 'no_inbound' ? 'disabled' : 'hidden'
    if (!meta && aiOff.value) return 'hidden'
    if (draftLoading.value[id]) return 'generating'
    const box = draftBoxes.value[id]
    if (!box || !box.open || box.stale) return 'collapsed'
    if (box.error) return 'error'
    return details.value[id]?.ai?.draft ? 'ready' : 'none'
  }

  /**
   * The collapsed row ("ให้ AI ร่างคำตอบ") was pressed: show the cached plain draft of this message, or ask the AI now —
   * a plain draft, never tied to the script (a cached "เรียบเรียงใหม่" result is not reopened here).
   */
  function openDraft(id: string): Promise<void> {
    const box = draftBoxes.value[id]
    if (box && box.resultFor && !box.stale && !box.error && !box.script && details.value[id]?.ai) {
      box.open = true
      return Promise.resolve()
    }
    return requestDraft(id)
  }

  /** "ปิด": back to the quiet row; the result stays cached (reopening shows it without a new request). */
  function closeDraft(id: string): void {
    const box = boxOf(id)
    box.open = false
    box.error = null
  }

  /** A customer wrote again: the cached draft answered an older message — collapse, drop a request in flight. */
  function staleDraft(id: string): void {
    const box = draftBoxes.value[id]
    if (draftLoading.value[id]) {
      draftSeq[id] = (draftSeq[id] ?? 0) + 1
      draftLoading.value[id] = false
      delete draftPolling[id]
    }
    if (box) Object.assign(box, { open: false, stale: !!box.resultFor, error: null, used: false })
  }

  const draftTimers: Record<string, ReturnType<typeof setTimeout>> = {}
  function scheduleDraft(id: string): void {
    if (draftDelayMs.value <= 0 || convs.value[id]?.ai_mode) return
    clearTimeout(draftTimers[id])
    draftTimers[id] = setTimeout(() => {
      delete draftTimers[id]
      void loadDraft(id)
    }, draftDelayMs.value)
  }

  // ------------------------------------------------ composer
  function setText(id: string, text: string): void {
    const st = ensureComposer(id)
    st.text = text
    saveReplyDraft(id, text)
    if (text.trim()) useCollab().typing(id) // presence: "… กำลังพิมพ์ตอบอยู่" on colleagues' screens
    if (st.origin === 'ai' || st.origin === 'script') st.edited = text !== (st.draftText ?? '')
    if (text.trim() === '') {
      st.origin = 'manual'
      st.cannedId = null
      st.cannedVia = null
      st.draftText = null
      st.draftKind = null
      st.scriptId = null
      st.scriptGaps = null
    }
  }

  /**
   * "ใส่ในช่องพิมพ์" of a suggestion (stores/script.ts): never sends. `replace` = instead of what was typed · `append` =
   * after the staff's own words (they stay). Blanks ("[ค่าส่ง]") are remembered so the send waits until they are filled.
   */
  function useSuggestion(
    id: string,
    s: { text: string; id: string; gaps: string[]; style?: string | null },
    mode: 'replace' | 'append' = 'replace',
  ): void {
    const st = ensureComposer(id)
    // a suggestion added after another one: the earlier one's blanks still have to be filled before sending
    const kept = mode === 'append' && st.text.trim() !== '' ? (st.scriptGaps ?? []) : []
    if (mode === 'append' && st.text.trim() !== '') {
      setText(id, `${st.text.replace(/\s+$/, '')}\n${s.text}`)
      st.origin = 'script'
      st.edited = true // the staff's words + the suggestion
    } else {
      setText(id, s.text)
      st.origin = 'script'
      st.edited = false
      st.cannedId = null
      st.cannedVia = null
    }
    st.draftText = s.text
    st.draftKind = null
    st.scriptId = s.id
    st.scriptStyle = s.style ?? null
    st.adapted = false
    st.aiNext = false
    const gaps = [...new Set([...kept, ...s.gaps])]
    st.scriptGaps = gaps.length ? gaps : null
  }

  /** A blank of a suggestion still in the typing box ("[ค่าส่ง]"), or null — sending waits for it. */
  function openGap(id: string): string | null {
    const st = composers.value[id]
    if (!st?.scriptGaps?.length) return null
    return st.scriptGaps.find((g) => st.text.includes(g)) ?? null
  }

  /**
   * "ใช้ร่างนี้": the draft in the box goes into the typing box (`replace` = instead of what was typed, `append` = after
   * it). The send then carries `from_ai_draft: true`, and Jev's quick answer its `canned_id` + `canned_via: 'ai_suggest'`.
   * The files that ride along (quick answer pictures, `ai.media`) are staged by the composer.
   */
  function useDraft(id: string, mode: 'replace' | 'append' = 'replace', gaps: string[] = []): boolean {
    const ai = details.value[id]?.ai
    const draft = ai?.draft
    if (!ai || !draft) return false
    const st = ensureComposer(id)
    const quick = ai.draft_source === 'canned' && ai.quick ? ai.quick : null
    const typed = st.text.trim() !== ''
    // "เรียบเรียงใหม่" keeps the suggestion's blanks ("[ค่าส่ง]"): like the suggestion, sending waits for them
    const blanks = [...new Set([...(mode === 'append' && typed ? (st.scriptGaps ?? []) : []), ...gaps])]
    st.scriptGaps = blanks.length ? blanks : null
    if (mode === 'append' && typed) {
      st.text = `${st.text.replace(/\s+$/, '')}\n${draft}`
      st.edited = true // the staff's words + the draft: counts as an edited draft
    } else {
      st.text = draft
      st.edited = false
      st.cannedId = null
      st.cannedVia = null
    }
    st.origin = 'ai'
    st.draftText = draft
    st.draftKind = quick ? 'quick' : ai.draft_source === 'menu_round' ? 'menu' : 'ai'
    // "ปรับตามแนวลูกค้า": the send counts it under the suggestion it re-toned, with the style (docs/script.md)
    const box0 = draftBoxes.value[id]
    st.adapted = !!ai.adapt && !!box0?.script && box0.script !== AI_NEXT
    // "ให้ AI พาไปขั้นต่อไป": counted as `ai_next` (with the customer's reply type, read by the API)
    st.aiNext = !!ai.next_step && box0?.script === AI_NEXT
    if (st.adapted) {
      st.scriptId = box0!.script!
      st.scriptStyle = ai.adapt!.style
    } else if (st.aiNext) {
      st.scriptId = AI_NEXT
      st.scriptStyle = null
    } else if (st.scriptId === AI_NEXT) {
      st.scriptId = null
    }
    if (quick) {
      st.cannedId = quick.canned_id
      st.cannedVia = 'ai_suggest'
    }
    const box = boxOf(id)
    box.used = true
    box.open = false
    return true
  }

  /**
   * Canned reply: replaces an untouched AI draft or an empty box, otherwise is inserted at the
   * caret (or appended).
   */
  function useCanned(id: string, c: CannedReply, caret?: { start: number; end: number }, via: CannedVia = 'button'): void {
    const st = ensureComposer(id)
    const replace = st.text.trim() === '' || ((st.origin === 'ai' || st.origin === 'script') && !st.edited)
    if (replace) {
      st.text = c.text
      st.origin = 'canned'
    } else {
      const start = caret?.start ?? st.text.length
      const end = caret?.end ?? start
      const before = st.text.slice(0, start)
      const after = st.text.slice(end)
      const pad = before && !/\s$/.test(before) ? ' ' : ''
      const padAfter = after && !/^\s/.test(after) ? ' ' : ''
      st.text = `${before}${pad}${c.text}${padAfter}${after}`
    }
    st.cannedId = c.id
    st.cannedVia = via
  }

  /** Put the server's copy of a message in the thread: replaces its temporary bubble, never a newer copy. */
  function placeServerMessage(id: string, msg: ChatMessage, replaceId: string | null): void {
    const cur = threads.value[id] ?? (threads.value[id] = [])
    const known = cur.findIndex((x) => x.id === msg.id)
    const i = replaceId ? cur.findIndex((x) => x.id === replaceId) : -1
    if (known >= 0) {
      // realtime was faster and is at least as new as this HTTP answer
      if (i >= 0 && i !== known) cur.splice(i, 1)
      return
    }
    if (i >= 0) cur.splice(i, 1, msg)
    else {
      cur.push(msg)
      cur.sort((a, b) => a.at.localeCompare(b.at))
    }
  }

  // If the WebSocket missed message.updated (reconnecting, laptop asleep), a bubble would stay "กำลังส่ง…":
  // re-read the newest page of the thread a few times while any of our messages is still pending.
  const PENDING_CHECKS_MS = [4000, 12000, 30000, 60000]
  const pendingChecks = new Map<string, ReturnType<typeof setTimeout>>()

  /** Re-read the newest page of a loaded thread: newer states replace ours, missed items are added. */
  async function refreshThread(id: string): Promise<void> {
    if (!threads.value[id]) return
    const page = await api().listMessages(id, { limit: 50 })
    const cur = threads.value[id] ?? []
    let added = false
    for (const fresh of page.items) {
      const i = cur.findIndex((x) => x.id === fresh.id)
      const old = cur[i]
      if (i >= 0 && old) {
        if (!old.pending) cur.splice(i, 1, fresh)
        continue
      }
      const tmp = fresh.kind === 'message' ? findTemp(cur, fresh) : -1
      if (tmp >= 0) cur.splice(tmp, 1, fresh)
      else cur.push(fresh)
      added = true
    }
    if (added) cur.sort((a, b) => a.at.localeCompare(b.at))
  }

  function watchPending(id: string, step = 0): void {
    if (pendingChecks.has(id) || step >= PENDING_CHECKS_MS.length) return
    const timer = setTimeout(async () => {
      pendingChecks.delete(id)
      const stillPending = () =>
        (threads.value[id] ?? []).some((x) => x.kind === 'message' && !isLocalOnly(x) && x.send?.status === 'pending')
      if (!stillPending()) return
      try {
        await refreshThread(id)
      } catch {
        /* next check */
      }
      if (stillPending()) watchPending(id, step + 1)
    }, PENDING_CHECKS_MS[step])
    pendingChecks.set(id, timer)
  }

  async function postSend(id: string, body: SendMessageBody, prev: ConversationSummary | undefined,
                          restore: (() => void) | null): Promise<boolean> {
    // one send = the text bubble + one bubble per library picture ("<client_id>:m<n>")
    const temps = sendClientIds(body).map(tempId)
    try {
      const res = await api().sendMessage(id, body)
      const got = res.messages?.length ? res.messages : [res.message]
      for (const m of got) placeServerMessage(id, m, tempId(m.send?.client_id ?? body.client_id))
      upsert(res.conversation)
      scheduleRefresh()
      if (got.some((m) => m.send?.status === 'pending')) watchPending(id)
      return true
    } catch (e) {
      const cur = threads.value[id] ?? []
      const mine = cur.filter((x) => temps.includes(x.id))
      if (prev) upsert(prev)
      if (e instanceof ApiError && e.status >= 400 && e.status < 500) {
        // refused before anything was stored (24 ชม., too long …): the text goes back to the box to edit
        if (restore) {
          for (const x of mine) cur.splice(cur.indexOf(x), 1)
          restore()
        } else {
          for (const x of mine) Object.assign(x, { pending: false, failed: true, refused: true, localError: errText(e) })
        }
      } else {
        // unknown whether the server got it: keep the bubble (no "ซ่อน" — retyping would send it twice);
        // "ลองใหม่" re-posts the same client_id, which the API answers with the stored message if it arrived
        for (const item of mine) {
          Object.assign(item, {
            pending: false,
            failed: true,
            refused: false,
            localError: `${errText(e)} (อาจส่งไปแล้ว กดลองใหม่ได้ ระบบไม่ส่งซ้ำ)`,
          })
        }
      }
      flash(`ส่งไม่สำเร็จ: ${errText(e)}`)
      return false
    }
  }

  async function send(id: string | null = selectedId.value, opts: SendOptions = {}): Promise<boolean> {
    if (!id) return false
    const st = ensureComposer(id)
    const text = st.text.trim()
    const media = opts.media ?? []
    if (!text && !media.length) return false
    if (text.includes(NUMBER_MARK)) {
      opts.onRefused?.()
      flash('ร่างยังมี [เช็คตัวเลข] ใส่ตัวเลขที่ถูกต้องก่อนส่ง')
      return false
    }
    const gap = openGap(id)
    if (gap) {
      flash(gapFlash(gap))
      return false
    }
    // collision guard: someone else answered this customer in the last 30 s → ask first (stores/collab.ts)
    if (!opts.force) {
      const clash = useCollab().collisionFor(id)
      if (clash) {
        opts.onRefused?.() // the staged pictures go back to the composer while we ask ("ส่งต่อ" takes them again)
        useCollab().askCollision(id, clash)
        return false
      }
    }
    const author = me.value ? { id: me.value.id, name: me.value.name } : { id: 'me', name: 'ฉัน' }
    const clientId = newClientId()
    const at = new Date(clock.now).toISOString()
    // text taken from the draft box ("ใช้ร่างนี้", maybe edited) counts as an AI draft reply; Jev's quick answer taken
    // that way counts as that library answer ("ai_suggest", set by useDraft)
    const fromDraft = st.origin === 'ai'
    const cannedId = st.cannedId
    const body: SendMessageBody = {
      text,
      client_id: clientId,
      from_ai_draft: fromDraft,
      canned_id: cannedId,
      canned_via: cannedId ? (st.cannedVia ?? 'button') : null,
    }
    // started from a script suggestion ("ใส่ในช่องพิมพ์", maybe edited): which one, so the owner can count what sells —
    // or its "ปรับตามแนวลูกค้า" version ("ใช้ร่างนี้"), with the customer's style to compare per style
    if (st.origin === 'script' && st.scriptId) body.script_suggestion_id = st.scriptId
    else if (st.origin === 'ai' && st.adapted && st.scriptId) {
      body.script_suggestion_id = st.scriptId
      body.script_adapted = true
    } else if (st.origin === 'ai' && st.aiNext) body.script_suggestion_id = AI_NEXT
    if (body.script_suggestion_id && st.scriptStyle) body.script_style = st.scriptStyle
    if (media.length) body.media_ids = media.map((m) => m.id)
    const bubble = (cid: string, type: ChatMessage['type'], t: string | null, atts: ChatMessage['attachments']): UiItem => ({
      kind: 'message',
      id: tempId(cid),
      conversation_id: id,
      direction: 'out',
      type,
      text: t,
      attachments: atts,
      at,
      author: { kind: 'staff', staff: author },
      read: false,
      send: { status: 'pending', error: null, client_id: cid },
      pending: true,
      sendBody: body,
    }) as UiItem
    const list = threads.value[id] ?? (threads.value[id] = [])
    if (text) list.push(bubble(clientId, 'text', text, []))
    media.forEach((m, i) =>
      list.push(bubble(mediaClientId(clientId, i + 1, !!text), m.kind, null, [
        { type: m.kind, url: m.url, name: m.title, preview_url: m.preview_url, media_id: m.id, width: m.width, height: m.height },
      ])),
    )
    const prevComposer = { ...st }
    Object.assign(st, {
      text: '', origin: 'manual', edited: false, cannedId: null, cannedVia: null, draftText: null, draftKind: null,
      scriptId: null, scriptGaps: null, scriptStyle: null, adapted: false, aiNext: false,
    })
    saveReplyDraft(id, '') // sent: the kept draft goes (a refused send puts the text back, and keeps it again)
    const prev = convs.value[id]
    if (prev) {
      const lastMedia = media[media.length - 1]
      upsert({
        ...prev,
        status: 'open',
        needs_reply: false,
        waiting_since: null,
        unread: 0,
        last_message: lastMedia
          ? { text: lastMedia.kind === 'video' ? '[วิดีโอ]' : '[รูปภาพ]', type: lastMedia.kind, direction: 'out', at }
          : { text, type: 'text', direction: 'out', at },
        last_message_at: at,
      })
    }
    return postSend(id, body, prev, () => {
      if (!st.text) {
        Object.assign(st, prevComposer)
        saveReplyDraft(id, st.text)
      }
      opts.onRefused?.()
    })
  }

  /** "ลองใหม่" on a failed bubble. */
  async function retrySend(id: string, itemId: string): Promise<void> {
    const cur = threads.value[id]
    const item = cur?.find((x) => x.id === itemId)
    if (!cur || !item || item.kind !== 'message' || item.pending) return
    if (isLocalOnly(item)) {
      // never confirmed: post the same body again — the API returns the stored message if it did arrive
      if (!item.sendBody) return
      Object.assign(item, { pending: true, failed: false, localError: null })
      await postSend(id, item.sendBody, undefined, null)
      return
    }
    item.pending = true
    try {
      const res = await api().retryMessage(itemId)
      const now = threads.value[id]?.find((x) => x.id === itemId)
      // realtime may already have delivered a newer state (sent) — only replace our in-flight copy
      if (now?.pending) {
        const list2 = threads.value[id] ?? []
        list2.splice(list2.indexOf(now), 1, res.message)
        list2.sort((a, b) => a.at.localeCompare(b.at))
      }
      upsert(res.conversation)
      scheduleRefresh()
      watchPending(id)
    } catch (e) {
      const now = threads.value[id]?.find((x) => x.id === itemId)
      if (now) now.pending = false
      flash(`ลองใหม่ไม่สำเร็จ: ${errText(e)}`)
    }
  }

  function dismissFailed(id: string, itemId: string): void {
    const cur = threads.value[id]
    if (!cur) return
    const i = cur.findIndex((x) => x.id === itemId)
    if (i >= 0) cur.splice(i, 1)
  }

  // ------------------------------------------------ conversation actions
  async function patch(id: string, p: ConversationPatch, optimistic: () => void, undo: () => void): Promise<void> {
    optimistic()
    try {
      upsert(await api().updateConversation(id, p))
      scheduleRefresh()
    } catch (e) {
      undo()
      flash(`บันทึกไม่สำเร็จ: ${errText(e)}`)
    }
  }

  function setStatus(id: string, status: 'open' | 'closed'): Promise<void> {
    const c = convs.value[id]
    if (!c) return Promise.resolve()
    const before = c.status
    return patch(
      id,
      { status },
      () => upsert({ ...c, status }),
      () => upsert({ ...c, status: before }),
    )
  }

  // the row carries these too (realtime): flip both, so a later local copy of the row (markRead …) does not undo it
  function toggleAutoCloseOff(id: string): Promise<void> {
    const d = details.value[id]
    if (!d) return Promise.resolve()
    const v = !d.auto_close_off
    const put = (x: boolean) => {
      d.auto_close_off = x
      const c = convs.value[id]
      if (c) c.auto_close_off = x
    }
    return patch(id, { auto_close_off: v }, () => put(v), () => put(!v))
  }

  function toggleFollowUp(id: string): Promise<void> {
    const d = details.value[id]
    if (!d) return Promise.resolve()
    const v = !d.follow_up.enabled
    const put = (x: boolean) => {
      d.follow_up.enabled = x
      const c = convs.value[id]
      if (c) c.follow_up = { ...(c.follow_up ?? d.follow_up), enabled: x }
    }
    return patch(id, { follow_up_enabled: v }, () => put(v), () => put(!v))
  }

  function toggleStep(id: string, stepId: string): Promise<void> {
    const step = details.value[id]?.ai?.next_steps.find((s) => s.id === stepId)
    if (!step) return Promise.resolve()
    const v = !step.done
    const before = { by: step.done_by ?? null, at: step.done_at ?? null }
    return patch(
      id,
      { next_step: { id: stepId, done: v } },
      () => {
        step.done = v
        step.done_by = me.value ? { id: me.value.id, name: me.value.name } : null
        step.done_at = new Date(clock.now).toISOString()
      },
      () => {
        step.done = !v
        step.done_by = before.by
        step.done_at = before.at
      },
    )
  }

  function reassign(id: string, staffId: string): Promise<void> {
    const c = convs.value[id]
    const m = team.value.find((t) => t.id === staffId)
    if (!c || !m) return Promise.resolve()
    const before = c.assigned_staff
    return patch(
      id,
      { assigned_staff_id: staffId },
      () => upsert({ ...c, assigned_staff: { id: m.id, name: m.name } }),
      () => upsert({ ...c, assigned_staff: before }),
    )
  }

  async function setAvailable(v: boolean): Promise<void> {
    const before = autoOff.value
    available.value = v
    autoOff.value = null
    if (me.value) me.value.available = v
    try {
      await api().setAvailability(v)
    } catch (e) {
      available.value = !v
      autoOff.value = before
      if (me.value) me.value.available = !v
      flash(`เปลี่ยนสถานะไม่สำเร็จ: ${errText(e)}`)
    }
  }

  /** The auto-off notice is shown (not closed by the person). */
  const autoOffNotice = computed(() => {
    const a = autoOff.value
    if (!a || available.value) return null
    return autoOffDismissed.value !== null && autoOffDismissed.value === (a.at ?? '') ? null : a
  })

  function dismissAutoOff(): void {
    autoOffDismissed.value = autoOff.value?.at ?? ''
  }

  /** WS staff.availability: a person's พร้อมรับ changed (their tap on another screen, or the system after no activity). */
  function applyAvailability(e: StaffAvailabilityEvent): void {
    const t = team.value.find((m) => m.id === e.staff.id)
    if (t) t.available = e.available
    if (!me.value || e.staff.id !== me.value.id) return
    me.value.available = e.available
    available.value = e.available
    autoOff.value =
      !e.available && e.reason === 'idle' ? { at: e.at, afterMin: e.after_min ?? settings.value.auto_off_min ?? 15 } : null
  }

  // ------------------------------------------------ right panel
  function reorder(id: string, order: PosOrder): void {
    ensureOrderForm(id)
    const form = orderForms.value[id]
    const pkgs = orderDrafts.value[id]?.packages ?? []
    const pkg = pkgs.find((p) => normPkg(p.name) === normPkg(order.package))
    if (form) {
      if (pkg) form.packageId = pkg.id
      form.reorderFrom = order.number
    }
    panelTab.value = 'order'
  }

  // ------------------------------------------------ search
  let searchSeq = 0
  async function runSearch(q: string): Promise<void> {
    query.value = q
    const seq = ++searchSeq
    if (q.trim().length < 2) {
      searchHits.value = null
      searching.value = false
      return
    }
    searching.value = true
    try {
      const res = await api().search(q.trim())
      if (seq === searchSeq) {
        searchHits.value = res.items
        searchPartial.value = res.partial === true
      }
    } catch (e) {
      if (seq === searchSeq) flash(`ค้นหาไม่สำเร็จ: ${errText(e)}`)
    } finally {
      if (seq === searchSeq) searching.value = false
    }
  }

  function clearSearch(): void {
    searchSeq++
    query.value = ''
    searchHits.value = null
    searchPartial.value = false
    searching.value = false
  }

  // ------------------------------------------------ realtime
  /** Other screens (comments) follow the same socket: they get every event and every resync. */
  const rtListeners = new Set<(e: RealtimeEvent | null) => void>()
  function onRealtime(fn: (e: RealtimeEvent | null) => void): () => void {
    rtListeners.add(fn)
    return () => rtListeners.delete(fn)
  }
  function tellListeners(e: RealtimeEvent | null): void {
    for (const fn of rtListeners) {
      try {
        fn(e)
      } catch {
        // a listener's failure must not break the inbox
      }
    }
  }

  function applyEvent(e: RealtimeEvent): void {
    tellListeners(e)
    upsert(e.conversation)
    if (e.type === 'message.created' || e.type === 'message.updated' || e.type === 'message.removed') {
      threadEvents[e.conversation_id] = (threadEvents[e.conversation_id] ?? 0) + 1
    }
    if (e.type === 'ai.steps') {
      // someone ticked "ขั้นต่อไปที่ AI แนะนำ" (maybe on another screen): saved ticks of the whole chat
      const ai = details.value[e.conversation_id]?.ai
      if (ai) {
        const byId = new Map(e.steps.map((t) => [t.id, t]))
        for (const s of ai.next_steps) {
          const t = byId.get(s.id)
          if (!t) continue
          s.done = t.done
          s.done_by = t.by
          s.done_at = t.at
        }
      }
      return
    }
    if (e.type === 'message.removed') {
      const list = threads.value[e.conversation_id]
      const i = list ? list.findIndex((x) => x.id === e.message_id) : -1
      if (list && i >= 0) list.splice(i, 1)
      scheduleRefresh()
      return
    }
    if (e.type === 'message.updated') {
      // send status changed (pending → sent / failed, or back to pending after "ลองใหม่")
      const list = threads.value[e.conversation_id]
      const i = list ? list.findIndex((x) => x.id === e.item.id) : -1
      if (list && i >= 0) {
        const old = list[i]
        list.splice(i, 1, e.item)
        if (old && old.at !== e.item.at) list.sort((a, b) => a.at.localeCompare(b.at))
      }
      // a read tick (the customer read up to this message): our earlier delivered messages are read too
      if (list && e.item.kind === 'message' && e.item.direction === 'out' && e.item.read) markReadUpTo(list, e.item.at)
      // the customer unsent a message (api/app/unsend.py): a cached draft may answer what is gone — back to the quiet row
      if (e.item.kind === 'message' && e.item.type === 'unsent' && e.item.direction === 'in') staleDraft(e.conversation_id)
      scheduleRefresh()
      return
    }
    if (e.type === 'message.created') {
      const list = threads.value[e.conversation_id]
      if (list && !list.some((x) => x.id === e.item.id)) {
        // our own pending send (same client_id) — or, for rows without one, the same text: swap it in place
        const item = e.item
        const byClient = item.kind === 'message' ? findTemp(list, item) : -1
        const pendingIdx =
          byClient >= 0
            ? byClient
            : item.kind === 'message' && item.direction === 'out' && !item.send?.client_id
            ? list.findIndex((x) => isLocalOnly(x) && x.pending === true && x.kind === 'message' && x.text === item.text)
            : -1
        if (pendingIdx >= 0) list.splice(pendingIdx, 1, item)
        else {
          list.push(item)
          list.sort((a, b) => a.at.localeCompare(b.at))
        }
      }
      const m = e.item as ChatMessage
      // the AI's bubble landed: its "AI กำลังพิมพ์" row goes (stores/aimode.ts)
      if (e.item.kind === 'message' && m.direction === 'out' && m.author.kind === 'ai') useAiMode().onAiBubble(e.conversation_id)
      if (e.item.kind === 'message' && m.direction === 'in') {
        staleDraft(e.conversation_id) // the cached draft answered an older message: back to the quiet row
        if (e.conversation_id === selectedId.value) {
          void markRead(e.conversation_id)
          scheduleDraft(e.conversation_id) // a read: shows a draft only if "ร่างอัตโนมัติ" made one
        }
      }
    }
    scheduleRefresh()
  }

  /** Read receipts are a watermark: every delivered message of ours up to `at` shows "ลูกค้าอ่านแล้ว". */
  function markReadUpTo(list: UiItem[], at: string): void {
    const upTo = Date.parse(at)
    for (const x of list) {
      if (x.kind !== 'message' || x.direction !== 'out' || x.read || isLocalOnly(x) || x.pending) continue
      if (x.send?.status === 'failed' || x.send?.status === 'pending' || Date.parse(x.at) > upTo) continue
      x.read = true
    }
  }

  // ------------------------------------------------ แนวลูกค้า (api/app/ai/script/style.py)
  /**
   * The customer's style of the chat's newest script (WS script.updated `style_key`, or the script just read): the row's
   * and the header's "ตอบภายใน 5 นาที" (lib/style.ts) follow it without waiting for the next list refresh.
   */
  function setStyleKey(id: string, key: CustomerStyleKey | null): void {
    for (const m of [convs.value[id], details.value[id]]) if (m && (m.style_key ?? null) !== key) m.style_key = key
  }

  // ------------------------------------------------ "ล้างแชทนี้ (ทดสอบ)" (api/app/devreset.py)
  /** A chat was deleted: by this screen's "ล้างแชท", or another screen's (WS conversation.removed). Idempotent. */
  function removeConversation(id: string, by: { id: string; name: string } | null = null): void {
    const known = id in convs.value || id in details.value
    const wasOpen = selectedId.value === id
    for (const m of [convs, details, threads, olderCursor, orderDrafts, orderForms, composers, aiMeta, draftBoxes]) delete m.value[id]
    if (draftTimers[id]) clearTimeout(draftTimers[id])
    delete draftTimers[id]
    if (wasOpen) {
      void select(null) // InboxView leaves /c/:id for the empty inbox
      flash(by && by.id !== meId.value ? `ล้างแชทแล้ว · โดย ${by.name}` : 'ล้างแชทแล้ว')
    }
    if (known) scheduleRefresh() // tab counts
  }

  // ------------------------------------------------ catching up (missed events)
  const detailSeq: Record<string, number> = {}
  /** Re-read a chat's detail (header, pins, switches, contact); the AI block (from …/draft) stays. */
  async function refreshDetail(id: string): Promise<void> {
    const n = (detailSeq[id] = (detailSeq[id] ?? 0) + 1)
    try {
      const detail = await api().getConversation(id)
      if (n !== detailSeq[id]) return
      const prev = details.value[id]
      if (prev) Object.assign(prev, { ...detail, ai: detail.ai ?? prev.ai ?? null })
      else details.value[id] = detail
      const cur = convs.value[id]
      const fresh = !cur || (detail.last_message_at ?? '') >= (cur.last_message_at ?? '')
      upsert(fresh ? toSummary(detail) : cur)
    } catch {
      /* the next resync / open reads it again */
    }
  }

  /** Everything on screen for the open chat, read again: thread, detail, notes, the follow-up card, the AI draft. */
  function reloadOpen(id: string): void {
    if (threads.value[id]) void refreshThread(id).catch(() => undefined)
    if (details.value[id]) void refreshDetail(id)
    const collab = useCollab()
    void collab.loadNotes(id)
    if (id in collab.followUps) void collab.loadFollowUp(id)
    if (!convs.value[id]?.ai_mode && !draftLoading.value[id]) void loadDraft(id)
  }

  /**
   * Events may have been missed (a reconnect, the socket's first open after the page loaded, the page back from being
   * hidden): the list, the team, the tag list, the open chat — and every other screen on the same socket (`null`).
   * Other loaded threads are re-read the next time they are opened.
   */
  function resync(): void {
    tellListeners(null)
    publishSocialSignal({ type: 'resync' }) // หน้าโพสต์ reads its queue again too (api/posts/live.ts)
    void fetchList({ silent: true })
    void loadTeam().catch(() => undefined)
    void useCollab().loadTags()
    for (const tid of Object.keys(threads.value)) if (tid !== selectedId.value) staleThreads.add(tid)
    if (selectedId.value) reloadOpen(selectedId.value)
  }

  let teamTimer: ReturnType<typeof setTimeout> | null = null
  /** WS team.changed (members, caps, shifts, routing settings): the team and the team-load strip, after a burst settles. */
  function onTeamChanged(): void {
    if (teamTimer) clearTimeout(teamTimer)
    teamTimer = setTimeout(() => {
      teamTimer = null
      void loadTeam().catch(() => undefined)
      void useRouting().refresh()
    }, 600)
  }

  // ------------------------------------------------ chats that change list with time (no event comes for them)
  let dueTimer: ReturnType<typeof setTimeout> | null = null
  let stopDueWatch: WatchStopHandle | null = null

  /**
   * One timer at the next moment a loaded chat moves by the clock (its follow-up due time, or the end of its follow-up
   * window): the row moves on the clock tick, then the list and the tab numbers are read again (+1.5 s, once the
   * server agrees) — and the chats of other lists that became due with it come in. The 30 s refresh covers the rest.
   */
  function armDueTimer(): void {
    if (dueTimer) clearTimeout(dueTimer)
    dueTimer = null
    const next = nextListChange(Object.values(convs.value), clock.now)
    if (next === null) return
    // long waits are re-checked hourly (the server-time offset may have been corrected meanwhile)
    const wait = Math.min(Math.max(0, next - clock.now) + DUE_SLACK_MS, 3600000)
    dueTimer = setTimeout(() => {
      dueTimer = null
      // fired before the moment (an hourly re-check, or the clock was corrected): just wait again
      if (nextListChange(Object.values(convs.value), clock.now) === next) armDueTimer()
      else void fetchList({ silent: true }).finally(armDueTimer)
    }, wait)
  }

  function watchDue(): void {
    stopDueWatch?.()
    stopDueWatch = watch(
      () =>
        Object.values(convs.value)
          .map((c) => (c.needs_reply || !c.follow_up_due_at ? '' : `${c.follow_up_due_at}~${c.follow_up_until ?? ''}`))
          .join('|'),
      () => armDueTimer(),
      { immediate: true },
    )
  }

  // ------------------------------------------------ lifecycle
  let rt: RealtimeConnection | null = null
  let periodic: ReturnType<typeof setInterval> | null = null
  /** Real input in any tab → `idle_s` in the heartbeat (lib/activity.ts). */
  let activity: ActivityTracker | null = null

  async function init(opts: { realtime?: boolean; tick?: boolean } = {}): Promise<void> {
    if (opts.tick !== false) clock.start()
    try {
      await loadTeam()
    } catch (e) {
      listError.value = errText(e)
    }
    await fetchList()
    void useCollab().init()
    if (opts.realtime !== false && !rt) {
      activity = new ActivityTracker({ onWake: () => rt?.ping?.() })
      activity.start()
      // the list above was read before the socket was open: whatever happened in between is read again on its first
      // open (later opens = reconnects resync through onResync)
      let opened = false
      rt = connectRealtime({
        onEvent: applyEvent,
        onResync: resync,
        onStatus: (s) => {
          realtimeStatus.value = s
          if (s === 'open' && !opened) {
            opened = true
            resync()
          }
        },
        onTeam: onTeamChanged,
        onAiDraft: (e) => onAiDraft(e.conversation_id),
        onSocial: (e) => publishSocialSignal(e), // หน้าโพสต์ changed: /posts reads it again (api/posts/live.ts)
        onCollab: (e) => useCollab().onEvent(e),
        onAiMode: (e) => useAiMode().onEvent(e), // docs/ai-mode.md: hand-off alert, side card refresh
        onRemoved: (e) => removeConversation(e.conversation_id, e.by), // "ล้างแชทนี้ (ทดสอบ)" on any screen
        onAvailability: applyAvailability, // พร้อมรับ auto-off / another screen of mine (docs/routing.md)
        onWebContext: (e) => useWebchat().onContext(e.conversation_id), // web chat card: the website sent news
        onScript: (e) => {
          // "สคริปต์แนะนำ" recomputed: the row's แนวลูกค้า at once, the script itself reloaded if open
          if (e.style_key !== undefined) setStyleKey(e.conversation_id, e.style_key)
          useScript().onUpdated(e.conversation_id)
        },
        pingExtra: () => ({ idle_s: activity?.idleSeconds() ?? 0 }),
      })
      useCollab().attach(rt)
      // counts/team loads also change with time (follow-up due, auto close)
      periodic = setInterval(() => {
        void fetchList({ silent: true })
        void loadTeam().catch(() => undefined)
      }, 30000)
      watchDue()
    }
  }

  function dispose(): void {
    useCollab().detach()
    rt?.close()
    rt = null
    activity?.stop()
    activity = null
    for (const t of pendingChecks.values()) clearTimeout(t)
    pendingChecks.clear()
    if (periodic) clearInterval(periodic)
    periodic = null
    if (refreshTimer) clearTimeout(refreshTimer)
    refreshTimer = null
    if (teamTimer) clearTimeout(teamTimer)
    teamTimer = null
    stopDueWatch?.()
    stopDueWatch = null
    if (dueTimer) clearTimeout(dueTimer)
    dueTimer = null
    for (const t of Object.values(draftTimers)) clearTimeout(t)
    clock.stop()
  }

  return {
    // state
    me,
    team,
    queueWaiting,
    settings,
    available,
    autoOff,
    autoOffNotice,
    cannedDefault,
    aiModeEnabled,
    convs,
    counts,
    tab,
    channel,
    scope,
    tagFilter,
    sortModes,
    listLoading,
    listError,
    selectedId,
    details,
    threads,
    olderCursor,
    orderDrafts,
    orderForms,
    composers,
    openError,
    panelTab,
    query,
    searchHits,
    searchPartial,
    searching,
    realtimeStatus,
    toast,
    refreshDelayMs,
    aiMeta,
    draftBoxes,
    aiOff,
    draftDelayMs,
    draftLoading,
    cursors,
    // getters
    meId,
    rows,
    groups,
    stickyNote,
    selected,
    selectedDetail,
    selectedThread,
    replyAlert,
    staffInfo,
    // actions
    flash,
    init,
    dispose,
    fetchList,
    loadMore,
    setTab,
    setChannel,
    setScope,
    setTagFilter,
    toggleSort,
    loadTeam,
    select,
    loadOlder,
    refreshThread,
    markRead,
    ensureComposer,
    loadDraft,
    onAiDraft,
    requestDraft,
    applyDraft,
    draftPhase,
    openDraft,
    closeDraft,
    setText,
    useDraft,
    useSuggestion,
    openGap,
    useCanned,
    send,
    retrySend,
    dismissFailed,
    setStatus,
    toggleAutoCloseOff,
    toggleFollowUp,
    toggleStep,
    reassign,
    setAvailable,
    dismissAutoOff,
    applyAvailability,
    reorder,
    runSearch,
    clearSearch,
    applyEvent,
    resync,
    refreshDetail,
    onTeamChanged,
    replaceItem,
    onRealtime,
    removeConversation,
    setStyleKey,
  }
})
